import { useParams, useNavigate } from 'react-router-dom';
import { useState, useContext, useEffect } from 'react';
import { useGet } from '@/query/fetchHooks';
import { Button, TEOLoader } from '@teo/components';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { NavContext } from '@/components/NavContext';
import CorrectionTrainingHeader from '@/components/LearningPath/Correction/CorrectionTrainingHeader';
import CorrectionTrainingContent from '@/components/LearningPath/Correction/CorrectionTrainingContent';
import { nthLastIndexOf } from '@/util';
import { axios } from '@/axios';
import { config } from '@/config';

function CorrectionTraining() {
  const { t } = useTranslation();
  const { setNavDisabled } = useContext(NavContext);
  const { organisationId, trainingId, studentId } = useParams();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;
  const [training, setTraining] = useState(null);
  const [followTraining, setFollowTraining] = useState(null);
  const [isUserFollow, setIsUserFollow] = useState(false);
  const [lpGroupId, setLpGroupId] = useState(null);
  const [student, setStudent] = useState();
  const [certificate, setCertificate] = useState(null);

  const navigate = useNavigate();

  const basePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 4)
  );

  const [learningpath, setLearningpath] = useState();
  const [remarks, setRemarks] = useState(null);
  const [pass, setPass] = useState(null);
  const [score, setScore] = useState(null);

  useGet(`/training/${trainingId}`, {
    retry: false,
    onSuccess: (result) => {
      setTraining(result?.data || {});
    },
  });

  useGet(`/nodegroups`, {
    type: 'training',
    objectId: trainingId,
    userId: studentId,
    onSuccess: (result) => {
      setLpGroupId(result?.data[0]?.lpgroupId || {});
    },
  });

  useGet(`/trainingfollow/${trainingId}/${studentId}`, {
    retry: false,
    onSuccess: async (res) => {
      setFollowTraining(res?.data || null);
      setScore(Math.round(res?.data?.score * 100) || 0);
      setRemarks(res?.data?.feedback ? res?.data?.feedback : null);
      setCertificate(
        res?.data?.certificates?.length > 0 ? res?.data?.certificates[0] : null
      );
      setIsUserFollow(true);
    },
    onError: (res) => {
      console.error('res', res);
      setIsUserFollow(false);
    },
  });

  useGet(`/memberlpgroupdetail/${organisationId}/${lpGroupId}`, {
    enabled: !!lpGroupId,
    onSuccess: (res) => {
      setLearningpath(res?.data || undefined);
    },
  });

  useGet(`/mylpfollows`, {
    enabled: !!lpGroupId,
    userId: authUser?.id,
    organisationId: authUser?.activeOrganisationId,
    lpgroupId: lpGroupId,
    studentId: studentId,
    onSuccess: (result) => {
      setStudent(result?.data[0] || null);
    },
  });

  const submitCorrection = async (submitScore) => {
    try {
      await axios.post(`${config.backend}/notifications`, {
        text: {
          text: t('notifications.teacher_corrected_training', {
            trainingName: training?.trainingName,
          }),
          icon: true,
        },
        type: 'trainingcorrected',
        globalLink: `/:lang/learn/:organisationId/learning-paths/${lpGroupId}/${
          training?.trainingType === 'training' ? 'trainings' : 'evaluations'
        }/${trainingId}`,
        userId: parseInt(studentId, 10),
        subject: parseInt(trainingId, 10),
      });

      await axios.put(
        `${config.backend}/trainingfollow/${followTraining?.trainingId}/${followTraining?.userId}`,
        {
          isCorrected: true,
          score: submitScore,
          maxScore: 1,
          feedback: remarks,
          certificates: certificate ? [certificate] : [],
          teacherId: authUser?.id,
        }
      );

      navigate(
        `${basePath}/learning-paths/correction/${learningpath?.id}/${student?.studentId}`
      );
    } catch (err) {
      console.error('Error during correction submission:', err);
    }
  };

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  });

  return (
    <div className="fixed top-0 left-0 z-10 h-screen w-screen bg-white">
      {!training && (
        <div className="m-auto flex min-h-[calc(100vh_-_150px)] p-10">
          <div className="m-auto">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        </div>
      )}
      {training && (
        <>
          <CorrectionTrainingHeader
            learningpath={learningpath}
            student={student}
            training={training}
          />
          <div className="h-full max-h-[calc(100vh_-_146px)] w-full overflow-y-auto pb-3">
            <CorrectionTrainingContent
              training={training}
              followTraining={followTraining}
              studentId={studentId}
              setRemarks={setRemarks}
              remarks={remarks}
              setPass={setPass}
              pass={pass}
              setScore={setScore}
              score={score}
              setCertificate={setCertificate}
              certificate={certificate}
            />
          </div>

          <div className=" flex w-full border-t border-grey-02">
            <div className="mx-auto flex w-full max-w-xl justify-between px-4 py-5">
              <Button
                variant="ghost"
                onClick={() =>
                  navigate(
                    `${basePath}/learning-paths/correction/${learningpath?.id}/${student?.studentId}`
                  )
                }
              >
                {t('button.cancel')}
              </Button>
              {!training?.inputScore && !training?.uploadCertificate && (
                <Button
                  disabled={!pass}
                  onClick={async () => {
                    const submitScore = pass === 'pass' ? 1 : 0;
                    submitCorrection(submitScore);
                  }}
                >
                  {t('pages.lp_training_correction.submit_type', {
                    type: t(`pages.lp_landing.${training?.trainingType}`),
                  })}
                </Button>
              )}
              {training?.inputScore && (
                <Button
                  disabled={
                    training?.inputScoreType === 'evaluator' && score === 0
                  }
                  onClick={async () => {
                    const submitScore = score / 100;
                    submitCorrection(submitScore);
                  }}
                >
                  {training?.inputScoreType === 'evaluator'
                    ? t('pages.lp_training_correction.submit_score')
                    : t('pages.lp_training_correction.validate_score')}
                </Button>
              )}
              {training?.uploadCertificate && !training?.inputScore && (
                <Button
                  disabled={!certificate}
                  onClick={async () => {
                    const submitScore = 1;
                    submitCorrection(submitScore);
                  }}
                >
                  {t('pages.lp_training_correction.validate')}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CorrectionTraining;
